import {Accordion, Button, HStack, Icon, Text, VStack} from "@chakra-ui/react"
import {HiOutlinePlus, HiOutlineDocumentDuplicate} from "react-icons/hi2";
import {useState} from "react";
import {RiDraggable} from "react-icons/ri";
import CreateEntityCard from "@/pages/scenario/components/CreateEntityCard";
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {SortableContext, verticalListSortingStrategy, useSortable, arrayMove} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

const SortableEntityItem = ({entity, index, entities, onEntityChange, onRemoveEntity, onDuplicateEntity, intents}) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: entity.id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Accordion.Item ref={setNodeRef} style={style} {...attributes} value={entity.id} bg={'bg.card'} borderRadius={16} mt={index > 0 ? 4 : 0}>
            <Accordion.ItemTrigger px="3" py={4}>
                <Icon fontSize={24} color={'gray.500'} {...listeners} cursor="grab" flexShrink={0}>
                    <RiDraggable />
                </Icon>
                <HStack w={'full'} justifyContent={'space-between'}>
                    <VStack alignItems={'flex-start'} gap={0} px={4} color={'white'}>
                        <Text>Entity #{index + 1}</Text>
                        <Text>{entity.name}</Text>
                    </VStack>
                    <HStack color={'gray.500'} alignItems={'center'} gap={4} mr={4}>
                        <Text>Required: {entity.isRequired ? "Yes" : "No"}</Text>
                        <Icon fontSize={20} cursor={'pointer'} onClick={(e) => { e.stopPropagation(); onDuplicateEntity(entity); }}>
                            <HiOutlineDocumentDuplicate />
                        </Icon>
                    </HStack>
                </HStack>
                <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
                <Accordion.ItemBody>
                    <CreateEntityCard
                        key={entity.id}
                        onChange={onEntityChange}
                        entity={entity}
                        onRemoveClick={() => onRemoveEntity(entity.id)}
                        canRemove={entities.length > 1}
                        currentEntityIndex={index}
                        intents={intents}
                        entities={entities}
                    />
                </Accordion.ItemBody>
            </Accordion.ItemContent>
        </Accordion.Item>
    );
};

const EntitiesAccordion = ({entities, onEntityChange, onRemoveEntity, onAddEntity, onReorder, onDuplicateEntity, intents}) => {
    const [visibleEntity, setVisibleEntity] = useState(entities[0]?.id);

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = ({active, over}) => {
        if (!over || active.id === over.id) return;
        const oldIndex = entities.findIndex(e => e.id === active.id);
        const newIndex = entities.findIndex(e => e.id === over.id);
        onReorder && onReorder(arrayMove(entities, oldIndex, newIndex));
    };

    return (
        <>
            <Text color={'white'} fontSize={18}>
                Entities
            </Text>

            <VStack py={6} w={'full'}>
                {(entities && entities.length > 0) &&
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={entities.map(e => e.id)} strategy={verticalListSortingStrategy}>
                            <Accordion.Root collapsible value={visibleEntity} onValueChange={(e) => setVisibleEntity(e.value)} variant={'none'} w={'full'}>
                                {entities?.map((entity, index) => (
                                    <SortableEntityItem
                                        key={entity.id}
                                        entity={entity}
                                        index={index}
                                        entities={entities}
                                        onEntityChange={onEntityChange}
                                        onRemoveEntity={onRemoveEntity}
                                        onDuplicateEntity={onDuplicateEntity}
                                        intents={intents}
                                    />
                                ))}
                            </Accordion.Root>
                        </SortableContext>
                    </DndContext>
                }

                <Button variant="primary" textDecoration='unset' mt={4} onClick={onAddEntity}>
                    <Icon fontSize={46}>
                        <HiOutlinePlus/>
                    </Icon>
                    <Text>Add another entity</Text>
                </Button>
            </VStack>
        </>
    )
}

export default EntitiesAccordion;
